/**
 * Personnel Module Page (Redesigned - Korat Health Style)
 *
 * Healthcare personnel overview with district map and detailed breakdown
 */

import { useState } from 'react'
import { Users, Stethoscope, UserCheck, TrendingUp, Building2 } from 'lucide-react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import {
  Sidebar,
  PageHeader,
  KpiCardRich,
  PersonnelMapPolygon,
  DataTable,
  YearFilter,
  DistrictFilter,
  ExportButton,
  type Column,
} from '../shared/components'
import { usePersonnelOverview, usePersonnelByDistrict, usePersonnelTrends, usePersonnelCategories, useDistricts } from '../services/api'
import type { PersonnelCategory } from '../types/modules'
import { formatNumber } from '../shared/utils/formatNumber'

export function PersonnelPage() {
  const [selectedYear, setSelectedYear] = useState(2567)
  const [selectedDistrict, setSelectedDistrict] = useState<number | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null)

  // Fetch data based on district selection
  // If district selected, fetch district-specific data; otherwise fetch overview
  const overviewQuery = usePersonnelOverview(selectedYear)
  const districtQuery = usePersonnelByDistrict(selectedDistrict || 0, selectedYear)

  // Use district data when district is selected, otherwise use overview
  const data = selectedDistrict ? districtQuery.data : overviewQuery.data
  const isLoading = selectedDistrict ? districtQuery.isLoading : overviewQuery.isLoading
  const error = selectedDistrict ? districtQuery.error : overviewQuery.error

  const { data: districts = [], isLoading: districtsLoading } = useDistricts()

  // Derive selected district name for map filtering
  const selectedDistrictName = selectedDistrict
    ? districts.find(d => d.id === selectedDistrict)?.name_th || null
    : null

  // Fetch personnel categories for dropdown
  const { data: categories = [] } = usePersonnelCategories() as { data: Array<{ id: number; name_th: string; name_en: string }> }

  // Fetch 5-year trends with dual Y-axis (staff count + ratio per 10k)
  const { data: trendsData, isLoading: trendsLoading } = usePersonnelTrends({
    districtId: selectedDistrict || undefined,
    categoryId: selectedCategory || undefined,
  })

  // Calculate personnel statistics
  const totalPopulation = 2700000
  const totalStaff = parseFloat(data?.totals?.grand_total || '0')
  const totalRatioPer10K = (totalStaff / totalPopulation) * 10000

  const doctorData = data?.by_category?.find((c) => c.abbreviation === 'MD')
  const doctorCount = parseFloat(String(doctorData?.total_staff || '0'))
  const doctorFulltime = parseFloat(String(doctorData?.total_fulltime || '0'))
  const doctorParttime = parseFloat(String(doctorData?.total_parttime || '0'))
  const doctorRatio = parseFloat(String(doctorData?.avg_ratio_per_10k || '0'))

  const nurseData = data?.by_category?.find((c) => c.abbreviation === 'RN')
  const nurseCount = parseFloat(String(nurseData?.total_staff || '0'))
  const nurseFulltime = parseFloat(String(nurseData?.total_fulltime || '0'))
  const nurseParttime = parseFloat(String(nurseData?.total_parttime || '0'))
  const nurseRatio = parseFloat(String(nurseData?.avg_ratio_per_10k || '0'))

  const pharmData = data?.by_category?.find((c) => c.abbreviation === 'PharmD')
  const ddsData = data?.by_category?.find((c) => c.abbreviation === 'DDS')
  const otherStaffCount = data?.by_category
    ?.filter((c) => !['MD', 'RN', 'PharmD', 'DDS'].includes(c.abbreviation))
    ?.reduce((sum, c) => sum + parseFloat(String(c.total_staff)), 0) || 0

  const supportStaffTotal = totalStaff - doctorCount - nurseCount

  const columns: Column<PersonnelCategory>[] = [
    {
      header: 'ประเภทบุคลากร',
      accessor: 'name_th',
      width: 'w-1/4',
      render: (value, row) => (
        <div>
          <div className="font-semibold text-korat-navy-700 tabular-nums">{value as string}</div>
          <div className="text-xs text-slate-500">{row.name_en} ({row.abbreviation})</div>
        </div>
      ),
    },
    {
      header: 'เต็มเวลา',
      accessor: 'total_fulltime',
      align: 'right',
      render: (value) => <span className="tabular-nums">{formatNumber(value as string)}</span>,
    },
    {
      header: 'บางเวลา',
      accessor: 'total_parttime',
      align: 'right',
      render: (value) => <span className="text-slate-600 tabular-nums">{formatNumber(value as string)}</span>,
    },
    {
      header: 'รวม',
      accessor: 'total_staff',
      align: 'right',
      render: (value) => (
        <span className="font-semibold text-korat-navy-700 tabular-nums">{formatNumber(value as string)}</span>
      ),
    },
    {
      header: 'อัตราส่วน/10K',
      accessor: 'avg_ratio_per_10k',
      align: 'right',
      width: 'w-32',
      render: (value) => (
        <span className="text-korat-orange-600 font-medium tabular-nums">
          {parseFloat(value as string).toFixed(2)}
        </span>
      ),
    },
  ]

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar currentPage="personnel" />

      <main className="flex-1 overflow-auto">
        <PageHeader
          icon={Users}
          title="บุคลากรสาธารณสุข"
          showFilters={true}
          selectedDistrict={
            selectedDistrict ? districts.find((d) => d.id === selectedDistrict)?.name_th : null
          }
          filters={
            <>
              <YearFilter selectedYear={selectedYear} onYearChange={setSelectedYear} />
              <DistrictFilter
                selectedDistrict={selectedDistrict}
                onDistrictChange={setSelectedDistrict}
                districts={districts}
                isLoading={districtsLoading}
              />
              <ExportButton module="personnel" year={selectedYear} districtId={selectedDistrict} />
            </>
          }
        />

        <div className="p-6 space-y-6">
          {/* Two-Column Layout: KPI Cards (40%) + Map (60%) */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left Column: KPI Cards (40% = 2/5) */}
            <div className="lg:col-span-2 space-y-6">
              {/* Total Personnel */}
              <KpiCardRich
                icon={Users}
                title="บุคลากรทั้งหมด"
                description="จำนวนบุคลากรสาธารณสุขทุกประเภททั้งเต็มเวลาและบางเวลา"
                value={data?.totals?.grand_total || 0}
                unit="คน"
                helperText="รวมทุกสถานพยาบาลในจังหวัด"
                breakdown={[
                  `<b>เต็มเวลา</b> ${formatNumber(data?.totals?.total_fulltime || 0)}`,
                  `<b>บางเวลา</b> ${formatNumber(data?.totals?.total_parttime || 0)}`,
                  `<b>อัตราส่วน</b> ${totalRatioPer10K.toFixed(1)} ต่อ 10K`,
                ]}
                isLoading={isLoading}
                variant="navy"
              />

              {/* Doctors */}
              <KpiCardRich
                icon={Stethoscope}
                title="แพทย์"
                description="แพทย์ทุกสาขาที่ปฏิบัติงานในพื้นที่"
                value={doctorCount}
                unit="คน"
                helperText={`อัตราส่วน ${doctorRatio.toFixed(2)} ต่อ 10K คน`}
                breakdown={[
                  `<b>เต็มเวลา</b> ${formatNumber(doctorFulltime)}`,
                  `<b>บางเวลา</b> ${formatNumber(doctorParttime)}`,
                  `<b>มาตรฐาน WHO</b> 10 ต่อ 10K`,
                ]}
                isLoading={isLoading}
                variant="orange"
              />

              {/* Nurses */}
              <KpiCardRich
                icon={UserCheck}
                title="พยาบาล"
                description="พยาบาลวิชาชีพที่ขึ้นทะเบียนประกอบวิชาชีพ"
                value={nurseCount}
                unit="คน"
                helperText={`อัตราส่วน ${nurseRatio.toFixed(2)} ต่อ 10K คน`}
                breakdown={[
                  `<b>เต็มเวลา</b> ${formatNumber(nurseFulltime)}`,
                  `<b>บางเวลา</b> ${formatNumber(nurseParttime)}`,
                  `<b>มาตรฐาน WHO</b> 25 ต่อ 10K`,
                ]}
                isLoading={isLoading}
                variant="blue"
              />

              {/* Support Staff */}
              <KpiCardRich
                icon={Building2}
                title="บุคลากรสนับสนุน"
                description="เภสัชกร เทคนิคการแพทย์ และบุคลากรสนับสนุนอื่นๆ"
                value={supportStaffTotal}
                unit="คน"
                helperText="เภสัชกร ทันตแพทย์ นักกายภาพบำบัด ฯลฯ"
                breakdown={[
                  `<b>เภสัชกร</b> ${formatNumber(pharmData?.total_staff || 0)}`,
                  `<b>ทันตแพทย์</b> ${formatNumber(ddsData?.total_staff || 0)}`,
                  `<b>อื่นๆ</b> ${formatNumber(otherStaffCount)}`,
                ]}
                isLoading={isLoading}
                variant="orange"
              />
            </div>

            {/* Right Column: Map (60% = 3/5) */}
            <div className="lg:col-span-3">
              <div className="sticky top-6">
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-korat-navy-700">
                    การกระจายบุคลากรตามพื้นที่
                  </h2>
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                    แผนที่แสดงสถานะความเพียงพอของบุคลากรสาธารณสุขในแต่ละอำเภอ
                  </p>
                </div>

                <PersonnelMapPolygon
                  year={selectedYear}
                  selectedDistrictId={selectedDistrict}
                  selectedDistrictName={selectedDistrictName}
                />
              </div>
            </div>
          </div>
          {/* End of two-column layout */}

          {/* Personnel by Category Table */}
          <section>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-korat-navy-700 tabular-nums mb-4">
                รายละเอียดตามประเภทบุคลากร
              </h2>
              <DataTable
                columns={columns}
                data={data?.by_category || []}
                isLoading={isLoading}
                error={error}
                emptyMessage="ไม่พบข้อมูลบุคลากร"
              />
            </div>
          </section>

          {/* Personnel Trends Chart with Dual Y-axis */}
          <section>
            <div className="bg-white rounded-xl border-2 border-korat-navy-200 shadow-sm p-6">
              <div className="mb-4 flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-korat-navy-700 tabular-nums">
                    แนวโน้มบุคลากร 5 ปี (พ.ศ. 2563-2567)
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">
                    จำนวนบุคลากรและอัตราส่วนต่อประชากร 10,000 คน
                  </p>
                </div>

                {/* Category Dropdown */}
                <div className="w-64">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    ตำแหน่ง
                  </label>
                  <select
                    value={selectedCategory || ''}
                    onChange={(e) => setSelectedCategory(e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-korat-navy-500 focus:border-korat-navy-500 text-sm"
                  >
                    <option value="">ทุกตำแหน่ง (รวม)</option>
                    {categories.map((cat: any) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name_th}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {trendsLoading ? (
                <div className="h-96 bg-slate-100 rounded animate-pulse" />
              ) : trendsData?.trends && trendsData.trends.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart
                    data={trendsData.trends.map((item: any) => ({
                      year: `${item.year}`,
                      staff_count: parseFloat(item.total_staff),
                      ratio: parseFloat(item.avg_ratio_per_10k),
                    }))}
                    margin={{ top: 5, right: 70, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="year"
                      stroke="#64748b"
                      style={{ fontSize: '14px', fontFamily: 'IBM Plex Sans Thai' }}
                    />
                    {/* Left Y-axis: Staff Count */}
                    <YAxis
                      yAxisId="left"
                      stroke="#1e3a8a"
                      style={{ fontSize: '14px', fontFamily: 'IBM Plex Sans Thai' }}
                      tickFormatter={(value) => formatNumber(value)}
                      label={{
                        value: 'จำนวนบุคลากร (คน)',
                        angle: -90,
                        position: 'insideLeft',
                        style: { fill: '#1e3a8a', fontFamily: 'IBM Plex Sans Thai', fontSize: '12px' }
                      }}
                    />
                    {/* Right Y-axis: Ratio per 10k */}
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      stroke="#ea580c"
                      style={{ fontSize: '14px', fontFamily: 'IBM Plex Sans Thai' }}
                      tickFormatter={(value) => value.toFixed(1)}
                      label={{
                        value: 'อัตราต่อประชากร 10,000 คน',
                        angle: 90,
                        position: 'insideRight',
                        style: { fill: '#ea580c', fontFamily: 'IBM Plex Sans Thai', fontSize: '12px' }
                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontFamily: 'IBM Plex Sans Thai',
                      }}
                      formatter={(value: any, name: string) => {
                        if (name === 'staff_count') return [formatNumber(value) + ' คน', 'จำนวนบุคลากร']
                        return [parseFloat(value).toFixed(2), 'อัตราต่อประชากร 10,000 คน']
                      }}
                      labelFormatter={(label) => `ปี พ.ศ. ${label}`}
                    />
                    <Legend
                      wrapperStyle={{
                        fontFamily: 'IBM Plex Sans Thai',
                        fontSize: '14px',
                      }}
                      iconType="line"
                    />
                    {/* Staff Count Line (Left Y-axis) */}
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="staff_count"
                      name="จำนวนบุคลากร (←แกนซ้าย)"
                      stroke="#1e3a8a"
                      strokeWidth={3}
                      dot={{ fill: '#1e3a8a', r: 5 }}
                      activeDot={{ r: 7 }}
                    />
                    {/* Ratio Line (Right Y-axis) */}
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="ratio"
                      name="อัตราต่อประชากร (แกนขวา→)"
                      stroke="#ea580c"
                      strokeWidth={3}
                      dot={{ fill: '#ea580c', r: 5 }}
                      activeDot={{ r: 7 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-96 flex items-center justify-center text-slate-500">
                  <div className="text-center">
                    <TrendingUp className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>ไม่พบข้อมูลแนวโน้ม</p>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </main>
    </div>
  )
}
